import { addDiagnosisApi } from 'api/DCK/addDiagnosisApi';
import { oauthApi } from 'api/Oauth/OauthApi';
import { LoginPageError } from 'constants/loginPageConstants';
import { mindupAdminMobilePaths, pagePaths } from 'constants/path';
import { getAuth, signInWithEmailAndPassword } from 'firebase/auth';
import { getPermission } from 'firebaseConfig';
import { LoginApiCallFunction } from 'models/DCK/DCKApiTypes';
import { useCallback } from 'react';
import { AccountLevel, AccountStatus } from 'utils/statusCode';
import packageJson from '../../../../package.json'; // package.json 파일을 가져옵니다.
import { useLocation } from 'react-router-dom';
import { userApi } from 'api/DCK/userApi';

// Login api call
export const useLoginApiHooks = () => {
    const { pathname } = useLocation();

    return useCallback<LoginApiCallFunction>(
        async (value, setLoginData, setErrMsg, navigate, setLoading, emailRef, passwordRef) => {
            // setLoading(true);
            const auth = getAuth();
            const response = await userApi.existUser(value.email);
            if (response.status === 'ok') {
                signInWithEmailAndPassword(auth, value.email, value.password)
                    .then(async (userCredential) => {
                        const version = packageJson.version;
                        // console.log(version);
                        const user = userCredential.user;
                        const idToken = await user.getIdToken();
                        const fcmToken = await getPermission();
                        const rqData = {
                            idToken: idToken,
                            deviceType: 3,
                            os: 'web',
                            model: 'chrome',
                            version: version,
                            token: fcmToken,
                        };
                        const response = await oauthApi.oauth(rqData);

                        if (response.status === 'ok') {
                            const getHospitalDataApiResponse = await addDiagnosisApi.hospitalFindById(response.data.user.hospitalId, response.data.bearerToken);
                            if (getHospitalDataApiResponse.status === 'ok') {
                                // TODO: token 테스트
                                // axios.defaults.headers.common['Authorization'] = `Bearer ${response.data.bearerToken}`;

                                setLoginData({
                                    id: response.data.user._id,
                                    email: response.data.user.email,
                                    hospitalName: response.data.user.hospitalName,
                                    hospitalId: response.data.user.hospitalId,
                                    level: response.data.user.level,
                                    mobile: response.data.user.mobile,
                                    name: response.data.user.name,
                                    status: response.data.user.status,
                                    token: response.data.bearerToken,
                                    doctors: getHospitalDataApiResponse.data.doctors,
                                    diagnosisTap: 0,
                                    hospitalServices: getHospitalDataApiResponse.data.services || [],
                                });
                                setLoading(false);

                                if (response.data.user.status === AccountStatus.active) {
                                    // 병원 서비스 확인
                                    const hospitalServices = getHospitalDataApiResponse.data.services || [];
                                    const hasDCK = hospitalServices.includes('DCK');
                                    const hasCL = hospitalServices.includes('CL');

                                    // 저장된 리다이렉트 URL 확인 및 처리
                                    const redirectUrl = localStorage.getItem('redirectAfterLogin');

                                    // 리다이렉트 URL 유효성 검사
                                    const isValidRedirectUrl = (url: string): boolean => {
                                        if (!url.startsWith('/')) return false;
                                        if (url.includes('//')) return false;
                                        return url.startsWith('/m.admin/mindup/');
                                    };

                                    // 리다이렉트 URL이 있고 유효한 경우
                                    if (redirectUrl && pathname === '/m.admin/mindup/login' && isValidRedirectUrl(redirectUrl)) {
                                        localStorage.removeItem('redirectAfterLogin');
                                        navigate(redirectUrl, { replace: true });
                                    } else {
                                        // 리다이렉트 URL이 있지만 사용하지 않는 경우 정리
                                        if (redirectUrl) {
                                            localStorage.removeItem('redirectAfterLogin');
                                        }

                                        if (response.data.user.level === AccountLevel.admin) {
                                            navigate(pagePaths.adminDiagnosisList, { replace: true });
                                        } else {
                                            switch (response.data.user.level) {
                                                case AccountLevel.sungnam:
                                                    navigate(pagePaths.diagnosisList, { replace: true });
                                                    break;
                                                case AccountLevel.externalCounselor:
                                                    if (pathname === '/m.admin/mindup/login') {
                                                        navigate(mindupAdminMobilePaths.mindUpAdminMobileUserReservationConsultation, { replace: true });
                                                    } else {
                                                        navigate(pagePaths.mindUpAdminTodayDiagnosis, { replace: true });
                                                    }
                                                    break;
                                                default:
                                                    // CL만 있는 경우 CLAdminManagePatient로 리다이렉트
                                                    if (!hasDCK && hasCL) {
                                                        navigate(pagePaths.CLAdminManagePatient, { replace: true });
                                                    } else {
                                                        navigate(pagePaths.dckHome, { replace: true });
                                                    }
                                                    break;
                                            }
                                        }
                                    }
                                    return getHospitalDataApiResponse.data;
                                } else {
                                    console.log('panding 상태, home으로 이동');
                                }
                            } else {
                                console.log('HospitalData 가져오기 실패');
                                setLoading(false);
                            }
                        } else {
                            console.log('oauth 로그인 실패');
                            setLoading(false);
                        }
                    })
                    .catch((error) => {
                        const errorCode = error.code;
                        const errorMessage = error.message;
                        console.log(errorCode, errorMessage, 'RtError');
                        // 비밀번호 체크
                        setErrMsg((prevErrMsg) => ({ ...prevErrMsg, password: LoginPageError.passwordCheckError }));
                        passwordRef.current?.focus();
                        setLoading(false);
                    });
            } else {
                // 아이디 중복체크로 아이디가 있는 아이디인지 없는 아이디인지 체크
                setErrMsg((prevErrMsg) => ({ ...prevErrMsg, email: LoginPageError.loginIdExistanceCheckError }));
                emailRef.current?.focus();
                setLoading(false);
            }
        },
        [pathname]
    );
};
